/**
 * Support Cases (/admin/support)
 *
 * FI-operator audience (fi_admin / fi_loan_officer / fi_compliance):
 *   GET /api/v1/support/fi-cases  (own FI-tenant only)
 *   Filters: status, priority
 *   "Open new case" button visible
 *
 * A session holding a platform-admin role sees the operator-console pointer
 * card and makes no request. GH #678 (operator ruling 2026-10-23) moved the
 * cross-institution queue (GET /api/v1/support/cases, the assignedToMe
 * filter) to platform-admin, platform.aaim.com /support; see
 * OperatorConsolePointer.tsx. Anyone else lands on the 403 fallback panel.
 *
 * Role compares lowercase both sides (`.claude/rules/authz-case-insensitive.md`).
 *
 * Types are imported directly from the generated openapi-typescript
 * artifact via `@aaimplatform/institutional-types/support-platform`.
 * No top-level view-model interfaces: per the W2d brief and the S3F
 * pre-commit incident, useState shapes are anonymous; only direct
 * `components['schemas']['...']` aliases are used as named types.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BodyText,
  Button,
  Badge,
  Card,
  DataTable,
  type DataTableColumn,
  Select,
  Alert,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/support-platform';
import { useAuthStore } from '../../stores/auth';
import { supportPlatformClient } from '../../api/client';
import { hasPlatformAdminRole } from '../../lib/platform-admin-roles';
import { SupportOperatorConsolePointer } from './OperatorConsolePointer';
import {
  SUPPORT_SERVICE_MISSING_TITLE,
  SupportServiceMissingError,
  SupportServiceNotice,
  supportFailure,
} from './SupportServiceNotice';

type SupportCase = components['schemas']['SupportCase'];
type SupportCaseStatus = components['schemas']['SupportCaseStatus'];
type SupportCasePriority = components['schemas']['SupportCasePriority'];

// ---------------------------------------------------------------------------
// Audience role set (lowercase per authz-case-insensitive)
// ---------------------------------------------------------------------------

const FI_OPERATOR_ROLES = [
  'fi_admin',
  'fi_loan_officer',
  'fi_compliance',
] as const;

// ---------------------------------------------------------------------------
// Badge variant helpers
// ---------------------------------------------------------------------------

function statusVariant(status: SupportCaseStatus): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'open':
      return 'warning';
    case 'awaiting_user':
    case 'awaiting_fi_input':
      return 'info';
    case 'awaiting_platform_admin':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function priorityVariant(priority: SupportCasePriority): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (priority) {
    case 'P1':
      return 'error';
    case 'P2':
      return 'warning';
    case 'P3':
      return 'info';
    case 'P4':
      return 'neutral';
    default:
      return 'neutral';
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SupportCasesPage() {
  const user = useAuthStore((s) => s.user);
  const roles = useMemo(
    () => (user?.roles ?? []).map((r) => (typeof r === 'string' ? r.toLowerCase() : r)),
    [user],
  );
  const isPlatformAdmin = hasPlatformAdminRole(user?.roles);
  // The support service refuses platform-admin callers on /fi-cases, so a
  // platform-admin session is never served the FI queue, FI role or not.
  const isFiOperator = !isPlatformAdmin && FI_OPERATOR_ROLES.some((r) => roles.includes(r));

  const [cases, setCases] = useState<SupportCase[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serviceMissing, setServiceMissing] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');

  const loadCases = useCallback(async () => {
    if (!isFiOperator) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setServiceMissing(false);
    try {
      // Own FI tenant only; the server derives the tenant.
      const fiParams: Record<string, string | number> = {};
      if (statusFilter) fiParams.status = statusFilter;
      if (priorityFilter) fiParams.priority = priorityFilter;
      const { data, error, response } = await supportPlatformClient.GET('/api/v1/support/fi-cases', {
        params: { query: fiParams as never },
      });
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to load cases');
      setCases(data?.cases ?? []);
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setLoadError(err instanceof Error ? err.message : 'Failed to load cases');
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [isFiOperator, statusFilter, priorityFilter]);

  useEffect(() => {
    void loadCases();
  }, [loadCases]);

  // -------------------------------------------------------------------------
  // Platform-admin session: case operations live in the operator console
  // -------------------------------------------------------------------------

  if (isPlatformAdmin) {
    return <SupportOperatorConsolePointer />;
  }

  // -------------------------------------------------------------------------
  // 403 fallback for anyone outside the FI-operator audience
  // -------------------------------------------------------------------------

  if (!isFiOperator) {
    return (
      <div className="p-6 space-y-4">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <Card>
          <div className="p-4">
            <Alert type="error">
              You do not have access to the support queue. It is reserved for Aaim support
              and for staff at your institution with the FI Admin, FI Loan Officer or FI
              Compliance role.
            </Alert>
          </div>
        </Card>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Table columns
  // -------------------------------------------------------------------------

  const columns: DataTableColumn<SupportCase>[] = [
    {
      header: 'Reference',
      accessor: 'reference',
      cell: (_value, row) => (
        <Link to={`/admin/support/${row.id}`} className="text-primary hover:underline">
          {row.reference}
        </Link>
      ),
    },
    {
      header: 'Category',
      accessor: 'category',
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (_value, row) => (
        <Badge variant={statusVariant(row.status)} size="sm">
          {row.status}
        </Badge>
      ),
    },
    {
      header: 'Priority',
      accessor: 'priority',
      cell: (_value, row) => (
        <Badge variant={priorityVariant(row.priority)} size="sm">
          {row.priority}
        </Badge>
      ),
    },
    {
      header: 'Opened',
      accessor: 'openedAt',
      cell: (_value, row) => (
        <span className="text-secondary text-sm">
          {row.openedAt ? new Date(row.openedAt).toLocaleString() : '-'}
        </span>
      ),
    },
    {
      header: 'Channel',
      accessor: 'sourceChannel',
    },
  ];

  // -------------------------------------------------------------------------
  // Filter option lists (anonymous shapes per ad-hoc-type rule)
  // -------------------------------------------------------------------------

  const statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'open', label: 'Open' },
    { value: 'awaiting_user', label: 'Awaiting user' },
    { value: 'awaiting_platform_admin', label: 'Awaiting Aaim support' },
    { value: 'awaiting_fi_input', label: 'Awaiting your input' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ];

  const priorityOptions = [
    { value: '', label: 'All priorities' },
    { value: 'P1', label: 'P1 (critical)' },
    { value: 'P2', label: 'P2 (high)' },
    { value: 'P3', label: 'P3 (normal)' },
    { value: 'P4', label: 'P4 (low)' },
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <BodyText size="sm" color="secondary">
            Cases opened by your institution. Aaim support handles resolution.
          </BodyText>
        </div>
        <Link to="/admin/support/new">
          <Button variant="primary">Open new case</Button>
        </Link>
      </div>

      {serviceMissing && <SupportServiceNotice />}
      {loadError && <Alert type="error">{loadError}</Alert>}

      <Card>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Status"
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
            <Select
              label="Priority"
              options={priorityOptions}
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            />
          </div>

          <DataTable<SupportCase>
            columns={columns}
            data={cases}
            rowKey="id"
            striped
            hoverable
            emptyMessage={
              loading
                ? 'Loading...'
                : serviceMissing
                  ? `${SUPPORT_SERVICE_MISSING_TITLE}.`
                  : 'No cases match the current filters.'
            }
          />
        </div>
      </Card>
    </div>
  );
}
